import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Tabs, Table, Tag, Button, Space, Select, Input, Form, InputNumber, Switch, message, Popconfirm, Typography, Progress } from 'antd';
import apiClient from '../../../shared/services/apiClient';
import { adminPath } from '../../../shared/config/paths';

const { Text } = Typography;
const API = '/api/admin/s3';

const size = (b) => {
    const n = Number(b || 0);
    if (n < 1024) return `${n} B`;
    const u = ['KB', 'MB', 'GB', 'TB', 'PB'];
    let v = n / 1024;
    let i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 ? 2 : 1)} ${u[i]}`;
};
const ok = (r) => r.data?.success;
const err = (r, fallback = '失败') => message.error(r?.data?.error || r?.data?.message || fallback);

const REGION_STATUS = { active: ['green', '对外开放'], maintenance: ['orange', '维护中'], disabled: ['default', '未开放'] };

/** 对象存储（S3）管理：地域 / 桶 / 密钥 / 用量 / 价格设置（服务端 api app/service/s3，运维见 api/docs/S3.md） */
const Regions = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        apiClient.get(`${API}/regions`).then((r) => ok(r) && setRows(r.data.data)).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);
    const setStatus = async (id, status) => update(id, { status });
    const update = async (id, body) => {
        try {
            const r = await apiClient.put(`${API}/regions/${id}`, body);
            if (ok(r)) { message.success('已更新'); load(); } else err(r);
        } catch (e) { err(e.response); }
    };
    const health = async (id) => {
        try {
            const r = await apiClient.get(`${API}/regions/${id}/health`);
            if (ok(r)) { message.success(`${r.data.data.status}：${r.data.data.storageNodesUp}/${r.data.data.storageNodes} 节点在线`); load(); } else err(r);
        } catch (e) { err(e.response, '节点连不上'); }
    };
    return (
        <Table rowKey="id" loading={loading} dataSource={rows} pagination={false} scroll={{ x: 1000 }} columns={[
            { title: '地域', key: 'n', render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.name} ({r.code})</Text><Text type="secondary" copyable>{r.endpoint}</Text></Space> },
            { title: '节点', key: 'ip', render: (_, r) => <Space direction="vertical" size={0}><Text>{r.node_ipv4}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.node_ipv6}</Text></Space> },
            {
                title: '容量', key: 'cap', width: 220, render: (_, r) => (
                    <div>
                        <Progress percent={r.capacity_bytes ? Math.round((r.used_bytes / r.capacity_bytes) * 1000) / 10 : 0} size="small" />
                        <Text type="secondary" style={{ fontSize: 12 }}>磁盘 {size(r.used_bytes)} / {size(r.capacity_bytes)} · 客户数据 {size(r.stored_bytes)}</Text>
                    </div>
                ),
            },
            {
                title: '硬盘', key: 'h', width: 170, render: (_, r) => {
                    const h = r.health;
                    if (!h) return <Text type="secondary">-</Text>;
                    const md = (h.md || [])[0];
                    const bad = (h.disks || []).filter((d) => !d.ok || d.pending > 0 || d.grown > 20 || d.realloc > 20).length;
                    return (
                        <Space direction="vertical" size={0}>
                            {md ? (md.degraded ? <Tag color="red">RAID 降级 {md.members}</Tag>
                                : <Tag color="green">RAID 正常{md.progress ? ' · 同步中' : ''}</Tag>) : null}
                            <Text type={bad ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                {(h.disks || []).length} 块盘{bad ? `，${bad} 块异常` : '，全部正常'}
                            </Text>
                            {md?.progress && <Text type="secondary" style={{ fontSize: 12 }}>{md.progress.replace(/^\[.*?\]\s*/, '')}</Text>}
                        </Space>
                    );
                },
            },
            { title: '桶', dataIndex: 'buckets', width: 70 },
            { title: '证书到期', dataIndex: 'cert_expires_at', width: 170 },
            { title: '节点心跳', dataIndex: 'last_seen_at', width: 170 },
            {
                title: '状态', key: 's', width: 150, render: (_, r) => (
                    <Select size="small" value={r.status} style={{ width: 120 }} onChange={(v) => setStatus(r.id, v)}
                        options={Object.entries(REGION_STATUS).map(([v, [, l]]) => ({ value: v, label: l }))} />
                ),
            },
            {
                title: '对客户开放', key: 'l', width: 110, render: (_, r) => (
                    <Switch size="small" checked={!!r.listed} onChange={(v) => update(r.id, { listed: v ? 1 : 0 })} />
                ),
            },
            { title: '', key: 'a', width: 90, render: (_, r) => <Button size="small" onClick={() => health(r.id)}>体检</Button> },
        ]} />
    );
};

const Buckets = () => {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState({ page: 1, keyword: '' });
    const [loading, setLoading] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        apiClient.get(`${API}/buckets`, { params: q }).then((r) => ok(r) && setData(r.data.data)).finally(() => setLoading(false));
    }, [q]);
    useEffect(() => { load(); }, [load]);
    const setStatus = async (id, status) => {
        try {
            const r = await apiClient.put(`${API}/buckets/${id}/status`, { status });
            if (ok(r)) { message.success('已更新'); load(); } else err(r);
        } catch (e) { err(e.response); }
    };
    return (
        <>
            <Input.Search allowClear placeholder="桶名 / 邮箱 / 用户 ID / 内部名" style={{ width: 300, marginBottom: 12 }} onSearch={(keyword) => setQ({ page: 1, keyword })} />
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 1000 }}
                pagination={{ current: q.page, total: data.total, pageSize: 20, onChange: (page) => setQ({ ...q, page }) }}
                columns={[
                    { title: '桶', key: 'n', render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.internal_name}</Text></Space> },
                    { title: '用户', key: 'u', render: (_, r) => <span>{r.email} <Text type="secondary">#{r.user_id}</Text></span> },
                    { title: '地域', dataIndex: 'region', width: 70 },
                    { title: '大小', dataIndex: 'bytes', align: 'right', render: size },
                    { title: '对象数', dataIndex: 'objects', align: 'right' },
                    { title: '公开', dataIndex: 'public_access', width: 70, render: (v) => (v ? <Tag color="blue">公开</Tag> : '-') },
                    { title: '状态', dataIndex: 'status', width: 90, render: (v) => (v === 'suspended' ? <Tag color="red">已暂停</Tag> : <Tag color="green">正常</Tag>) },
                    { title: '创建', dataIndex: 'created_at', width: 170 },
                    {
                        title: '', key: 'a', width: 90, render: (_, r) => (r.status === 'suspended'
                            ? <Button size="small" onClick={() => setStatus(r.id, 'active')}>恢复</Button>
                            : (
                                <Popconfirm title="暂停后客户的密钥都访问不了这个桶，公开访问也关闭" onConfirm={() => setStatus(r.id, 'suspended')}>
                                    <Button size="small" danger>暂停</Button>
                                </Popconfirm>
                            )),
                    },
                ]} />
        </>
    );
};

const PERM = { read: '只读', readwrite: '读写', admin: '管理' };
const Keys = () => {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState({ page: 1, keyword: '' });
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        apiClient.get(`${API}/keys`, { params: q }).then((r) => ok(r) && setData(r.data.data)).finally(() => setLoading(false));
    }, [q]);
    return (
        <>
            <Input.Search allowClear placeholder="Access Key ID / 邮箱 / 名称" style={{ width: 300, marginBottom: 12 }} onSearch={(keyword) => setQ({ page: 1, keyword })} />
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 900 }}
                pagination={{ current: q.page, total: data.total, pageSize: 20, onChange: (page) => setQ({ ...q, page }) }}
                columns={[
                    { title: '名称', dataIndex: 'name' },
                    { title: 'Access Key ID', dataIndex: 'access_key_id', render: (v) => <Text code copyable>{v}</Text> },
                    { title: '用户', key: 'u', render: (_, r) => <span>{r.email} <Text type="secondary">#{r.user_id}</Text></span> },
                    { title: '权限', dataIndex: 'permission', width: 70, render: (v) => PERM[v] || v },
                    { title: '范围', dataIndex: 'scope', width: 90, render: (v) => (v === 'all' ? '全部桶' : '指定桶') },
                    { title: '状态', dataIndex: 'status', width: 80, render: (v) => (v === 'active' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>) },
                    { title: '最后使用', dataIndex: 'last_used_at', width: 170 },
                ]} />
        </>
    );
};

const Usage = () => {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState({ items: [] });
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        apiClient.get(`${API}/usage`, { params: { month } }).then((r) => ok(r) && setData(r.data.data)).finally(() => setLoading(false));
    }, [month]);
    const num = (v) => Number(v || 0).toLocaleString();
    return (
        <>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 180, marginBottom: 12 }} />
            <Table rowKey="user_id" loading={loading} dataSource={data.items} scroll={{ x: 900 }} pagination={{ pageSize: 50 }} columns={[
                { title: '用户', key: 'u', render: (_, r) => <span>{r.email} <Text type="secondary">#{r.user_id}</Text></span> },
                { title: '存储 GB·月', dataIndex: 'storage_gb_month', align: 'right' },
                { title: 'A 类操作', dataIndex: 'class_a', align: 'right', render: num },
                { title: 'B 类操作', dataIndex: 'class_b', align: 'right', render: num },
                { title: '直连流量 GB', dataIndex: 'egress_gb', align: 'right' },
                { title: '公开访问 GB', dataIndex: 'public_gb', align: 'right' },
            ]} />
        </>
    );
};

const Settings = () => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        apiClient.get(`${API}/settings`).then((r) => ok(r) && form.setFieldsValue({ ...r.data.data, enabled: !!r.data.data.enabled }));
    }, [form]);
    const save = async (v) => {
        setSaving(true);
        try {
            const r = await apiClient.put(`${API}/settings`, { ...v, enabled: v.enabled ? 1 : 0 });
            if (ok(r)) message.success('已保存'); else err(r);
        } catch (e) { err(e.response); } finally { setSaving(false); }
    };
    const usd = { min: 0, step: 0.001, addonBefore: '$', style: { width: 180 } };
    return (
        <Form form={form} layout="vertical" onFinish={save} style={{ maxWidth: 640 }}>
            <Form.Item name="enabled" label="允许新建桶" valuePropName="checked"><Switch /></Form.Item>
            <Text strong>价格（USD，月结）</Text>
            <Space wrap style={{ marginTop: 8 }}>
                <Form.Item name="price_storage_gb_month" label="存储 / GB·月"><InputNumber {...usd} /></Form.Item>
                <Form.Item name="price_class_a_million" label="A 类操作 / 百万次" tooltip="写入、列表（PUT/POST/LIST）"><InputNumber {...usd} /></Form.Item>
                <Form.Item name="price_class_b_million" label="B 类操作 / 百万次" tooltip="读取（GET/HEAD）"><InputNumber {...usd} /></Form.Item>
                <Form.Item name="price_egress_gb" label="下载流量 / GB" tooltip="S3 接口直接下载 + 公开桶 / 自定义域名经 CDN 的访客下载，合并计费；公开访问的请求次数计入 B 类操作"><InputNumber {...usd} /></Form.Item>
            </Space>
            <Text strong>每月免费额度（每个账号）</Text>
            <Space wrap style={{ marginTop: 8 }}>
                <Form.Item name="free_storage_gb" label="存储 GB·月"><InputNumber min={0} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="free_class_a" label="A 类操作次数"><InputNumber min={0} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="free_class_b" label="B 类操作次数"><InputNumber min={0} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="free_egress_gb" label="下载流量 GB"><InputNumber min={0} style={{ width: 150 }} /></Form.Item>
            </Space>
            <Text strong>每个账号上限</Text>
            <Space wrap style={{ marginTop: 8 }}>
                <Form.Item name="max_buckets" label="桶数"><InputNumber min={1} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="max_keys" label="密钥数"><InputNumber min={1} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="max_domains_per_bucket" label="每桶自定义域名数"><InputNumber min={0} style={{ width: 150 }} /></Form.Item>
                <Form.Item name="public_enabled" label="开放公开访问" valuePropName="checked" getValueProps={(v) => ({ checked: !!v })} normalize={(v) => (v ? 1 : 0)}><Switch /></Form.Item>
            </Space>
            <Text strong>欠费</Text>
            <Space wrap style={{ marginTop: 8 }}>
                <Form.Item name="hold_after_days" label="逾期几天后暂停" tooltip="账单到期日（出账后 7 天）之后再等几天，停用密钥和公开访问，数据保留">
                    <InputNumber min={0} max={90} addonAfter="天" style={{ width: 150 }} /></Form.Item>
                <Form.Item name="delete_after_days" label="暂停几天后删除数据" tooltip="0 = 不删除。删除前 3 天通知客户">
                    <InputNumber min={0} max={365} addonAfter="天" style={{ width: 150 }} /></Form.Item>
            </Space>
            <div><Button type="primary" htmlType="submit" loading={saving}>保存</Button></div>
        </Form>
    );
};

const TABS = [
    { key: 'regions', label: '地域', children: <Regions /> },
    { key: 'buckets', label: '桶', children: <Buckets /> },
    { key: 'keys', label: '密钥', children: <Keys /> },
    { key: 'usage', label: '用量', children: <Usage /> },
    { key: 'settings', label: '价格与限制', children: <Settings /> },
];

const S3Admin = () => {
    const { tab } = useParams();
    const navigate = useNavigate();
    const active = TABS.some((x) => x.key === tab) ? tab : 'regions';
    return (
        <Card title="对象存储（S3）">
            <Tabs activeKey={active} onChange={(k) => navigate(adminPath(`/s3/${k}`))} items={TABS} destroyInactiveTabPane />
        </Card>
    );
};

export default S3Admin;
